import dayjs from 'dayjs';
import {
  DashboardEventDetailsUrl,
  EventPreview,
  EventVendorLocation,
  SearchCafeEvent,
  SearchCateringEvent,
  SearchDeliveryEvent,
  SearchEvent,
  SearchPopupEvent,
  TodoTaskEventVendor,
} from './types';
import { EventProduct, EventStatus } from '@/api';
import { CalendarEvent, CalendarEventDetailsUrl } from '../Calendar.page/types';
import { getCalendarEventStatus } from '../Calendar.page/helpers';

export const getLocationAddress = (location?: EventVendorLocation['location'] | null) => {
  if (!location) return '';
  return [location.building.name, location.name !== location.building.name ? location.name : null]
    .filter(Boolean)
    .join(', ');
};

export const getFullLocationAddress = (location?: EventVendorLocation['location'] | null) => {
  if (!location) return '';
  return [
    location.building.name,
    `${location.name !== location.building.name ? location.name : ''} ${
      location.building.address.address1 || location.building.address.address2
    }`,
    `${location.building.address.city} ${location.building.address.state} ${location.building.address.zipCode}`,
  ]
    .filter((string) => string.trim())
    .join(', ');
};

const checkEventCompleted = ({
  productType,
  foodDelivered,
  calendarEvent,
  eventStatus,
  completedEvents,
}: {
  productType: EventProduct;
  foodDelivered?: boolean;
  calendarEvent: boolean;
  eventStatus: EventStatus;
  completedEvents: boolean;
}): boolean => {
  if (productType === EventProduct.DELIVERY && foodDelivered) {
    return true;
  }
  if (calendarEvent) {
    return eventStatus === EventStatus.COMPLETE || eventStatus === EventStatus.FINAL;
  }
  return completedEvents;
};

const getEventDetailsUrl = ({
  productType,
  completedEvents,
  calendarEvent,
  eventId,
  eventStatus,
  foodDelivered,
}: {
  productType: EventProduct;
  completedEvents: boolean;
  calendarEvent: boolean;
  eventId: string;
  eventStatus: EventStatus;
  foodDelivered?: boolean;
}) => {
  const isCompleted = checkEventCompleted({
    productType,
    foodDelivered,
    calendarEvent,
    eventStatus,
    completedEvents,
  });

  const urlMap: Record<
    EventProduct,
    {
      dashboardCompleted: string;
      dashboardUpcoming: string;
      calendarCompleted: string;
      calendarUpcoming: string;
    }
  > = {
    [EventProduct.POPUP]: {
      dashboardCompleted: DashboardEventDetailsUrl.POPUP_COMPLETED,
      dashboardUpcoming: DashboardEventDetailsUrl.POPUP_UPCOMING,
      calendarCompleted: CalendarEventDetailsUrl.POPUP_COMPLETED,
      calendarUpcoming: CalendarEventDetailsUrl.POPUP_UPCOMING,
    },
    [EventProduct.CAFE]: {
      dashboardCompleted: DashboardEventDetailsUrl.CAFE_COMPLETED,
      dashboardUpcoming: DashboardEventDetailsUrl.CAFE_UPCOMING,
      calendarCompleted: CalendarEventDetailsUrl.CAFE_COMPLETED,
      calendarUpcoming: CalendarEventDetailsUrl.CAFE_UPCOMING,
    },
    [EventProduct.CATERING]: {
      dashboardCompleted: DashboardEventDetailsUrl.CATERING_COMPLETED,
      dashboardUpcoming: DashboardEventDetailsUrl.CATERING_UPCOMING,
      calendarCompleted: CalendarEventDetailsUrl.CATERING_COMPLETED,
      calendarUpcoming: CalendarEventDetailsUrl.CATERING_UPCOMING,
    },
    [EventProduct.DELIVERY]: {
      dashboardCompleted: DashboardEventDetailsUrl.DELIVERY_COMPLETED,
      dashboardUpcoming: DashboardEventDetailsUrl.DELIVERY_UPCOMING,
      calendarCompleted: CalendarEventDetailsUrl.DELIVERY_COMPLETED,
      calendarUpcoming: CalendarEventDetailsUrl.DELIVERY_UPCOMING,
    },
  };

  const urls = urlMap[productType];

  if (calendarEvent) {
    return `${isCompleted ? urls.calendarCompleted : urls.calendarUpcoming}${eventId}`;
  }

  return `${isCompleted ? urls.dashboardCompleted : urls.dashboardUpcoming}${eventId}`;
};

type AggregatableEvent = {
  id: string;
  productType: EventProduct;
  aggregateManifestKey?: string;
};

export const aggregateDeliveryEventVendors = <T extends AggregatableEvent>(
  events: T[] | null | undefined
): T[] => {
  if (!events) return [];

  // The surviving card represents the whole manifest, so it accumulates every
  // member's id. Event detail views split this back apart to request them all.
  // Grouped events are copied rather than mutated; a lone event passes through.
  const indexByKey = new Map<string, number>();
  const result: T[] = [];

  for (const event of events) {
    const key =
      event?.productType === EventProduct.DELIVERY ? event.aggregateManifestKey : undefined;

    if (!key) {
      result.push(event);
      continue;
    }

    const index = indexByKey.get(key);
    if (index === undefined) {
      indexByKey.set(key, result.length);
      result.push(event);
      continue;
    }

    const survivor = result[index];
    result[index] = { ...survivor, id: `${survivor.id},${event.id}` };
  }

  return result;
};

export const getEventPreview = (
  events: SearchEvent[] | CalendarEvent[],
  completedEvents = false,
  calendarEvent = false
) => {
  if (!events) return [];

  const preparedEvents: EventPreview[] = events.map((event) => {
    const previewInfo = {
      eventTime: '',
      eventType: event.productType,
      cateringEventName: '',
      accountName: '',
      address: '',
      setupTime: '',
      restaurantName: '',
      manifestTime: '',
      readyAndBaggedTime: '',
      eventDetailsUrl: '',
      id: event.id,
      vendorId: event.vendor.id,
      status: null,
    };

    switch (event.productType) {
      case EventProduct.POPUP:
        const popupEventVendor = event as SearchPopupEvent;
        const popupEvent = popupEventVendor.popupEvent;
        const popupEventStatus = calendarEvent
          ? getCalendarEventStatus({
              event: popupEventVendor,
              status: popupEvent.status.toUpperCase(),
            })
          : null;
        const popupEventPreview = {
          ...previewInfo,
          eventTime: dayjs(popupEvent.startTime)
            .subtract(popupEventVendor.eventLocation?.setupTime ?? 0, 'minute')
            .toISOString(),
          accountName: popupEvent.account.name,
          address: getLocationAddress(popupEventVendor.eventLocation?.location),
          setupTime: dayjs(popupEvent.startTime)
            .subtract(popupEventVendor.eventLocation?.setupTime ?? 0, 'minutes')
            .format('hh:mma'),
          restaurantName: event.vendor?.internalName ?? '',
          eventDetailsUrl: getEventDetailsUrl({
            productType: event.productType,
            completedEvents,
            calendarEvent,
            eventId: event.id,
            eventStatus: popupEvent.status.toUpperCase() as EventStatus,
          }),
          status: popupEventStatus,
          productTypeId: popupEvent.id,
        };
        return popupEventPreview;
      case EventProduct.CAFE:
        const cafeEventVendor = event as SearchCafeEvent;
        const cafeEvent = cafeEventVendor.popupEvent;
        const cafeEventStatus = calendarEvent
          ? getCalendarEventStatus({
              event: cafeEventVendor,
              status: cafeEvent.status.toUpperCase(),
            })
          : null;
        const cafeStationStart =
          cafeEventVendor.eventLocation?.serviceStartTime ?? cafeEvent.startTime;
        const cafeEventPreview = {
          ...previewInfo,

          eventTime: dayjs(cafeStationStart)
            .subtract(cafeEventVendor.eventLocation?.setupTime ?? 0, 'minute')
            .toISOString(),
          accountName: cafeEvent.account.name,
          address: getLocationAddress(cafeEventVendor.eventLocation?.location),
          setupTime: dayjs(cafeStationStart)
            .subtract(cafeEventVendor.eventLocation?.setupTime ?? 0, 'minutes')
            .format('hh:mma'),
          restaurantName: event.vendor?.internalName ?? '',
          eventDetailsUrl: getEventDetailsUrl({
            productType: event.productType,
            completedEvents,
            calendarEvent,
            eventId: event.id,
            eventStatus: cafeEvent.status.toUpperCase() as EventStatus,
          }),
          status: cafeEventStatus,
          productTypeId: cafeEvent.id,
        };
        return cafeEventPreview;
      case EventProduct.CATERING:
        const cateringVendor = event as SearchCateringEvent;
        const cateringEvent = cateringVendor.event;
        const cateringEventStatus = calendarEvent
          ? getCalendarEventStatus({ event: cateringVendor, status: cateringEvent.status })
          : null;
        const cateringEventPreview = {
          ...previewInfo,

          eventTime: cateringEvent.startTime,
          accountName: cateringEvent.account.name,
          cateringEventName: cateringEvent.name ?? '',
          address: getLocationAddress(cateringEvent.location),
          setupTime: cateringEvent.setupTime
            ? dayjs(cateringEvent.setupTime).format('hh:mma')
            : dayjs(cateringEvent.startTime).format('hh:mma'),
          restaurantName: event.vendor?.internalName ?? '',
          eventDetailsUrl: getEventDetailsUrl({
            productType: event.productType,
            completedEvents,
            calendarEvent,
            eventId: event.id,
            eventStatus: cateringEvent.status,
          }),
          status: cateringEventStatus,
          productTypeId: cateringEvent.id,
        };
        return cateringEventPreview;
      case EventProduct.DELIVERY:
        const deliveryVendor = event as SearchDeliveryEvent;
        const deliveryEvent = deliveryVendor.selectEvent;
        const deliveryEventStatus = calendarEvent
          ? getCalendarEventStatus({ event: deliveryVendor, status: deliveryEvent.status })
          : null;
        const deliveryEventPreview = {
          ...previewInfo,

          eventTime: deliveryEvent.startTime,
          restaurantName: event.vendor?.internalName ?? '',
          readyAndBaggedTime: deliveryEvent.readyAndBaggedTime
            ? dayjs(deliveryEvent.readyAndBaggedTime).format('hh:mma')
            : '',
          manifestTime: dayjs(deliveryEvent.startTime).format('hh:mma'),
          eventDetailsUrl: getEventDetailsUrl({
            productType: event.productType,
            completedEvents,
            calendarEvent,
            eventId: event.id,
            eventStatus: deliveryEvent.status,
            foodDelivered: deliveryVendor.foodDelivered ?? false,
          }),
          status: deliveryEventStatus,
          productTypeId: deliveryEvent.id,
        };
        return deliveryEventPreview;
    }
  });

  return preparedEvents;
};

export const getEndListText = (events: number, month: boolean) => {
  const start = events > 1 ? 'These are' : 'This is';
  const end = month ? 'in the last month' : 'from the previous 2 business days';
  const s = events > 1 ? 's' : '';

  return `${start} your most recent event${s} ${end}.`;
};

export const getTodoDetails = ({ popupEvent, eventLocation, vendor }: TodoTaskEventVendor) => {
  return [
    popupEvent.productType === EventProduct.POPUP ? popupEvent.account.name : null,
    eventLocation?.location.name,
    vendor.internalName,
  ]
    .filter(Boolean)
    .join(' • ');
};
